export type MathSegment =
  | { type: 'text'; content: string }
  | { type: 'math'; content: string; display: boolean };

/**
 * Parse text content to extract math expressions and plain text segments.
 * Handles ZWSP (Zero-Width Space, \u200B) as delimiter around math expressions.
 *
 * Supported patterns:
 * - ZWSP-delimited: \u200B...\u200B (anything wrapped in ZWSP is treated as math)
 * - Display Math: $$...$$
 * - Inline Math: $...$
 * - Escaped Dollar: \$
 *
 * @param content - The text content to parse (may contain ZWSP and LaTeX)
 * @returns Array of segments with type 'text' or 'math'
 */
export const parseMathContent = (content: string): MathSegment[] => {
  // Regex to match ZWSP-delimited content, Display Math, Inline Math, and Escaped Dollar
  // Pattern explanation:
  // 1. (\u200B[^\u200B]+\u200B) - Content wrapped in ZWSP (treated as math)
  // 2. (\$\$[\s\S]*?\$\$) - Display Math
  // 3. (\$(?![\d\s])(?:[^$\n]*[^$\n\s])?\$) - Inline Math, excludes currency ($5)
  //    `[^$\n]*[^$\n\s]` keeps the original "closing `$` cannot follow whitespace"
  //    constraint without lookbehind. iOS Safari < 16.4 / LINE in-app browser
  //    throws SyntaxError on `(?<...)` at module load (regression of QUIZ-APP-73D
  //    after PR #2468 fixed the same pattern in `useZepBanWords`).
  // 4. (\\$) - Escaped dollar sign
  const MATH_REGEX =
    /(\u200B[^\u200B]+\u200B)|(\$\$[\s\S]*?\$\$)|(\$(?![\d\s])(?:[^$\n]*[^$\n\s])?\$)|(\\$)/g;

  const parts = content.split(MATH_REGEX);
  const segments: MathSegment[] = [];

  for (const part of parts) {
    // Skip only undefined, null, or empty string
    if (!part) continue;

    // Check for Escaped Dollar (\$)
    if (part === '\\$') {
      segments.push({ type: 'text', content: '$' });
      continue;
    }

    // ZWSP-delimited content: treat as math
    if (part.startsWith('\u200B') && part.endsWith('\u200B')) {
      // Remove ZWSP only, preserve all other whitespace
      const content = part.replace(/\u200B/g, '');

      // Check if it's Display Math ($$...$$)
      if (content.startsWith('$$') && content.endsWith('$$')) {
        segments.push({
          type: 'math',
          content: content.slice(2, -2),
          display: true,
        });
      } else if (content.startsWith('$') && content.endsWith('$')) {
        // Inline Math with $ delimiters
        segments.push({
          type: 'math',
          content: content.slice(1, -1),
          display: false,
        });
      } else {
        // No $ delimiters, but ZWSP-wrapped → treat as inline math
        segments.push({
          type: 'math',
          content: content,
          display: false,
        });
      }
      continue;
    }

    // No ZWSP: Standard math detection
    // Display Math ($$...$$)
    if (part.startsWith('$$') && part.endsWith('$$')) {
      segments.push({
        type: 'math',
        content: part.slice(2, -2),
        display: true,
      });
      continue;
    }

    // Inline Math ($...$) with currency protection
    if (part.startsWith('$') && part.endsWith('$') && !part.includes('$$')) {
      segments.push({
        type: 'math',
        content: part.slice(1, -1),
        display: false,
      });
      continue;
    }

    // Plain text - preserve all whitespace!
    segments.push({ type: 'text', content: part });
  }

  return segments;
};
